import React from 'react';
import type { FolderColor } from '../../utils/workspaceFolders';
import { colorTone } from './colors';

/**
 * The mark of a project: a tile in the project's colour with its initial, the same in the
 * sidebar, the path on the canvas, the cards and the table, so a project is told from a folder
 * (the folder icon) and from a workspace (its picture) before its name is read. Styles: .ns-lib-tile.
 */
export const ProjectTile: React.FC<{ name: string; color?: FolderColor; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ name, color, size = 'md', className = '' }) => {
    const initial = (name.trim().charAt(0) || '·').toUpperCase();
    return (
        <span className={`ns-lib-tile ${size === 'sm' ? 'ns-lib-tile-sm' : size === 'lg' ? 'ns-lib-tile-lg' : ''} ${className}`} data-tone={colorTone(color)} aria-hidden="true">
            {initial}
        </span>
    );
};

/** The mark of a workspace: its newest picture, or the dot grid of an empty canvas. */
export const WorkspaceThumb: React.FC<{ thumbnail?: string; px?: number; className?: string }> = ({ thumbnail, px = 18, className = '' }) => (
    <span className={`ns-lib-thumb flex-none ${className}`} style={{ width: px, height: px }} aria-hidden="true">
        {thumbnail ? (
            <img src={thumbnail} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" draggable={false} />
        ) : (
            <svg viewBox="0 0 24 24" width={Math.round(px * 0.6)} height={Math.round(px * 0.6)} fill="currentColor" aria-hidden="true">
                {[5, 12, 19].flatMap(x => [5, 12, 19].map(y => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.7" />))}
            </svg>
        )}
    </span>
);

/** The people a project is shared with, as a count on a people mark. */
export const PeopleBadge: React.FC<{ count: number; title?: string; className?: string }> = ({ count, title, className = '' }) => (count > 0 ? (
    <span className={`ns-lib-people ${className}`} title={title ?? `Shared with ${count} ${count === 1 ? 'person' : 'people'}`} data-people-count={count}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><circle cx="17" cy="9" r="2.5" /><path d="M16 15.5a5 5 0 0 1 5.5 4.5" /></svg>
        {count}
    </span>
) : null);
